import { createMachine, assign } from 'xstate';
import { stringify, parse } from 'query-string';
import axios, { CancelTokenSource } from 'axios';
import {
	exportDataByUrl,
	exportDataHighcharts,
} from 'components/StatisticService/helpers';
import Axios from 'axios';

// cancelTokenSource moved to machine context for per-instance isolation
// See context initialization below

export interface DataModel {
	areas: OptionData[] | null;
	products: OptionData[] | null;
	units: OptionData[] | null;
	distributedUnits: OptionData[] | null;
	time: OptionDataTimeGroup | null;
	parameters: OptionDataParameterGroup[] | null;
	filters: OptionDataFiltersGroup[] | null;
	searchFilters: any;
	measurements: OptionData[] | null;
	variables: OptionData[] | null;
	// result: Result | null;
	result: any;
	resultError: errorModel | null;
	lastUpdated: string | null;
	isSurvey: boolean | null | undefined;
	organizationsFilter: FilterData | null;
	questionsFilter: FilterData | null;
	categoriesFilter: FilterData | null;
}

export interface FilterData {
	text: string;
	type: string;
	options: ItemsData[];
}

export interface ItemsData {
	label: string;
	count: number;
	value: string;
}

export interface errorModel {
	statusCode: number;
	message: string;
}

export interface OptionData {
	id: string;
	name: string;
	value: string;
	text: string;
	disabled: boolean;
	isSurvey?: boolean;
	isHidden?: boolean;
	description?: string;
}

export interface OptionDataTimeGroup {
	years: OptionData[] | null;
	fragments: OptionData[] | null;
}

export interface OptionDataFiltersGroup {
	query?: string;
	offset?: number;
	fetch?: number;
	total?: number;
	apiUrl?: string;
	isHidden?: boolean;
	selectedBuckets?: number;
	text: string;
	type: 'multiselect' | 'multiselectSearch';
	items: OptionData[];
}

export interface OptionDataParameterGroup {
	heading?: string;
	text?: string;
	description?: string;
	dimensions?: OptionDataParameterDimension[];
}

export interface OptionDataParameterDimension {
	heading: string;
	text?: string;
	description?: string;
	properties: OptionData[];
}
export interface ResultData {
	CalendarYear: string;
	Amount: string;
	[key: string]: string;
}

export interface Result {
	headers: ResultData;
	data: ResultData[];
}

type resultFormats = 'table' | 'matrixtable' | 'chart';
type chartTypes = 'bar' | 'column' | 'pie' | 'line';
type filterType = { [key: string]: string[] };

export interface Context extends DataModel {
	apiUrl: string;
	router: any;
	resultHeading: string;
	error: string | null;
	reqCancelled: boolean;
	cancelTokenSource: CancelTokenSource;
	selectedArea: string | null;
	selectedProduct: string | null;
	selectedUnit: string | null;
	selectedDistributedUnit: string | null;
	selectedYear: string[];
	selectedTimeFragment: string[];
	selectedMeasurement: string | null;
	selectedParameters: string[];
	selectedFilters: string[];
	selectedVariable: string | null;
	selectedResultFormat: resultFormats;
	selectedChartType: chartTypes | null;
	selectedChartStacked: boolean;
	selectedCategoryFilter: string | null;
	selectedQuestionFilter: string | null;
	selectedOrganizationFilter: string[] | null;
	order: string | null;
	numItemsToFetch: number;
	numItemsTotal: number;
	itemsPerPage: number;
	lastUpdated: string | null;
	wizardStep: number;
	wizardStepInfo: any;
	maxWizardSteps: number;
	currentWizardStepIndex: number;
	wizardSteps: any[];
	wizardSelections: any[];
	pendingWizardSelections: WizardSelectionState[];
	wizardFilters: FilterData | null;
	wizardFilterParamKeys: string[];
	isWizardActive: boolean;
}

interface LoadApiData {
	(apiUrl: string, context?: Context): Promise<OptionData[]>;
}

interface SearchApiData {
	(
		apiUrl: string,
		context: Context,
		itemsToFetch: number,
		filter: string,
		searchterm?: string
	): Promise<OptionData>;
}

interface WizardStepOption {
	label: string;
	value: string;
	count: number;
}

interface WizardSelectionState {
	paramKey: string;
	values: string[];
}

interface WizardStepCurrent {
	paramKey: string;
	label: string;
	dimension: string;
	category: string;
	isMultiSelect: boolean;
	options: WizardStepOption[];
}

interface WizardStepResponse {
	step: number;
	totalSteps: number;
	forcedColumn: string[];
	current: WizardStepCurrent;
	next: WizardStepCurrent | null;
	measurements: OptionData[] | null;
}

interface GetWizardStep {
	(
		apiUrl: string | null,
		context: Context,
		itemsToFetch?: number
	): Promise<WizardStepResponse>;
}

interface GetFilterData {
	(
		apiUrl: string | null,
		paramKey: string,
		context: Context,
		itemsToFetch?: number
	): Promise<any>;
}

type StatisticServiceEvent = {
	type: string;
	data?: any;
	value?: any;
	name?: string;
	itemsToFetch?: number;
	filter?: string;
	searchterm?: string;
	orderBy?: string;
	orderDirection?: string;
	chartRef?: any;
};

const getUrlQueries = (context: Context): string => {
	const query: any = {};
	if (context.selectedArea) {
		query.area = context.selectedArea;
	}
	if (context.selectedProduct) {
		query.product = context.selectedProduct;
	}
	if (context.selectedUnit) {
		query.unit = context.selectedUnit;
	}
	if (context.selectedDistributedUnit) {
		query.distributedUnit = context.selectedDistributedUnit;
	}
	if (context.selectedMeasurement) {
		query.measurement = context.selectedMeasurement;
	}
	if (context.selectedYear) {
		query.year = context.selectedYear;
	}
	if (
		context.selectedTimeFragment &&
		query.area &&
		query.product &&
		query.unit
	) {
		if (
			context.selectedTimeFragment.indexOf('Tidsperiod.Månad') !== -1 &&
			context.selectedResultFormat !== 'chart'
		) {
			query.timefragment = ['Tidsperiod.År', 'Tidsperiod.Månad'];
		} else {
			query.timefragment = context.selectedTimeFragment;
		}
	}
	if (context.selectedParameters) {
		query.param = context.selectedParameters;
	}
	if (context.selectedFilters) {
		query.filter = context.selectedFilters;
	}
	if (context.selectedVariable) {
		query.variable = context.selectedVariable;
	}

	// Include filter parameters in URL
	if (context.selectedCategoryFilter) {
		query.category = context.selectedCategoryFilter;
	}
	if (context.selectedQuestionFilter) {
		query.question = context.selectedQuestionFilter;
	}
	if (
		context.selectedOrganizationFilter &&
		context.selectedOrganizationFilter.length > 0
	) {
		query.organization = context.selectedOrganizationFilter.join(':');
	}

	query.resultFormat = context.selectedResultFormat;
	query.chartType = context.selectedChartType;
	query.chartStacked = context.selectedChartStacked;
	if (context.order) {
		query.orderBy = context.order;
	}
	if (context.numItemsToFetch) {
		query.fetch = context.numItemsToFetch;
	}

	return `?${stringify(query, { arrayFormat: 'none' })}`;
};

const loadApiData: LoadApiData = async (apiUrl, context) => {
	let query: string = '';
	if (context) {
		query = getUrlQueries(context);
	}
	const { data } = await axios({
		method: 'GET',
		url: `${apiUrl}${query}`,
		cancelToken: context?.cancelTokenSource?.token,
	});

	return data;
};

const searchApiData: SearchApiData = async (
	apiUrl,
	context,
	itemsToFetch,
	filter,
	searchterm
) => {
	apiUrl = '/api/sv/statisticsservice/bridgeapi/filter';

	// uncomment this below to be able to test with prep api and disable cors in the browser to make it work
	// apiUrl =
	// 	'https://integration.upphandlingsmyndigheten.se/api/sv/statisticsservice/bridgeapi/filter';
	// Change preprod to wwww to test with production
	// apiUrl:
	// 'https://preprod.upphandlingsmyndigheten.se/api/sv/statisticsservice/bridgeapi/statistics',

	let url = `${apiUrl}?area=${context.selectedArea}&distributedUnit=${
		context.selectedDistributedUnit
	}&dimension=${context.selectedParameters.filter((f: string) =>
		f.includes(filter)
	)}&product=${context.selectedProduct}&unit=${
		context.selectedUnit
	}&fetch=${itemsToFetch}&year=${context.selectedYear}`;

	if (searchterm) url += `&searchterm=${searchterm}`;

	// Fixed: Added missing & before filter parameters
	if (context.selectedCategoryFilter)
		url += `&categoriesfilter=${context.selectedCategoryFilter}`;
	if (context.selectedQuestionFilter)
		url += `&questionsfilter=${context.selectedQuestionFilter}`;
	if (context.selectedOrganizationFilter)
		url += `&organizationsfilter=${context.selectedOrganizationFilter}`;

	// if (itemsToFetch) url += `fetch=${itemsToFetch}`;

	const { data } = await axios({
		method: 'GET',
		url: url,
		cancelToken: context?.cancelTokenSource?.token,
	});

	return data;
};

const getWizardStep: GetWizardStep = async (apiUrl, context, itemsToFetch) => {
	apiUrl = '/api/sv/statisticsservice/bridgeapi/filter/wizard/step';

	// uncomment this below to be able to test with prep api and disable cors in the browser to make it work
	// apiUrl =
	// 	'https://integration.upphandlingsmyndigheten.se/api/sv/statisticsservice/bridgeapi/filter/wizard/step';
	// Change preprod to wwww to test with production
	// apiUrl: 'http://webapi-prep.upphandlingsmyndigheten.se/api/sv/statisticsservice/bridgeapi/filter/wizard/step';

	// Use number of loaded steps to determine which step to fetch next (max 8 steps)
	const currentStep = Math.min(context.wizardSteps.length, 7);
	let url = `${apiUrl}?area=${context.selectedArea}&distributedUnit=${context.selectedDistributedUnit}&product=${context.selectedProduct}&unit=${context.selectedUnit}&step=${currentStep}&fetch=${itemsToFetch}`;

	// Include filter parameters in wizard step calls
	if (context.selectedCategoryFilter) {
		url += `&category=${context.selectedCategoryFilter}`;
	}
	if (context.selectedQuestionFilter) {
		url += `&question=${context.selectedQuestionFilter}`;
	}
	if (
		context.selectedOrganizationFilter &&
		context.selectedOrganizationFilter.length > 0
	) {
		url += `&organization=${context.selectedOrganizationFilter.join(':')}`;
	}

	// Build dimension parameters from all wizard steps, not just selectedFilters
	// This ensures all previous selections are included in subsequent step requests
	let allDimensionFilters: string[] = [];

	if (context.wizardSteps && context.wizardSteps.length > 0) {
		context.wizardSteps.forEach((step, index) => {
			if (step.paramKey && context.wizardSelections[index]) {
				const paramKey = step.paramKey;
				const selectionValue = context.wizardSelections[index];
				const selectionArray = Array.isArray(selectionValue)
					? selectionValue
					: [selectionValue];

				selectionArray.forEach((val: string) => {
					allDimensionFilters.push(`${paramKey}|${val}`);
				});
			}
		});
	}

	// Add accumulated dimension parameters
	allDimensionFilters.forEach((filter: string) => {
		url += `&dimension=${encodeURIComponent(filter)}`;
	});

	// Add selections from previous wizard steps
	if (context.wizardSelections && context.wizardSelections.length > 0) {
		context.wizardSelections.forEach((selection: any, index: number) => {
			if (selection) {
				// Handle both single values and arrays (for multiselect steps)
				if (Array.isArray(selection)) {
					url += `&selection${index}=${encodeURIComponent(
						selection.join(';')
					)}`;
				} else {
					url += `&selection${index}=${encodeURIComponent(selection)}`;
				}
			}
		});
	}

	// if (itemsToFetch) url += `&fetch=${itemsToFetch}`;

	const { data } = await axios({
		method: 'GET',
		url: url,
		cancelToken: context?.cancelTokenSource?.token,
	});

	return data;
};

export const statisticServiceMachine = createMachine<
	Context,
	StatisticServiceEvent
>(
	{
		id: 'statisticServiceMachine',
		initial: 'idle',
		context: {
			apiUrl: '',
			router: null,
			resultHeading: 'Resultat',
			error: null,
			reqCancelled: false,
			cancelTokenSource: axios.CancelToken.source(),
			areas: null,
			products: null,
			units: null,
			distributedUnits: null,
			time: null,
			parameters: null,
			filters: null,
			searchFilters: null,
			measurements: null,
			variables: null,
			selectedArea: null,
			selectedProduct: null,
			selectedUnit: null,
			selectedDistributedUnit: null,
			selectedYear: [],
			selectedTimeFragment: ['Tidsperiod.År'],
			selectedMeasurement: '',
			selectedParameters: [],
			selectedFilters: [],
			selectedVariable: null,
			selectedResultFormat: 'table',
			selectedChartType: 'column',
			selectedChartStacked: false,
			selectedCategoryFilter: null,
			selectedQuestionFilter: null,
			selectedOrganizationFilter: null,
			result: null,
			order: null,
			numItemsToFetch: 2000,
			numItemsTotal: 0,
			itemsPerPage: 100,
			resultError: null,
			lastUpdated: null,
			isSurvey: false,
			categoriesFilter: null,
			questionsFilter: null,
			organizationsFilter: null,
			wizardStep: 0,
			wizardStepInfo: null,
			maxWizardSteps: 8,
			currentWizardStepIndex: 0,
			wizardSteps: [],
			wizardSelections: [],
			pendingWizardSelections: [],
			wizardFilters: null,
			wizardFilterParamKeys: [],
			isWizardActive: false,
		},
		states: {
			idle: {
				on: {
					INITIALIZE: 'initialize',
				},
			},

			initialize: {
				entry: ['setContextFromQuery'],
				invoke: {
					id: 'initialize',
					src: (context, _event) => loadApiData(context.apiUrl, context),
					onDone: {
						target: 'checkingFilterRestoration',
						actions: 'setData',
					},
					onError: {
						target: 'idle',
						actions: assign({
							error: (_context, event) => event.data.error,
						}),
					},
				},
			},
			checkingFilterRestoration: {
				always: [
					{
						target: 'loadingWizardStep',
						cond: 'shouldStartWizard',
					},
					{
						target: 'initialized',
					},
				],
			},

			initialized: {
				always: [
					{ target: 'result.success', cond: 'hasResult' },
					{
						target: 'loadingWizardStep',
						cond: 'shouldStartWizard',
					},
					{
						target: 'result.success',
						cond: 'hasDistributedUnits',
					},
					{ target: 'selectingUnit', cond: 'hasUnits' },
					{ target: 'selectingProduct', cond: 'hasProducts' },
					{ target: 'selectingArea', cond: 'hasAreas' },
					{ target: 'loadingAreas' },
				],
			},

			loadingAreas: {
				invoke: {
					id: 'getAreas',
					src: (context, _event) => loadApiData(context.apiUrl),
					onDone: {
						target: 'selectingArea',
						actions: 'setData',
					},
					onError: {
						target: 'errorArea',
						actions: assign({
							error: (_context, event) => {
								return `Error ${event.data.response.status} - ${event.data.response.statusText}`;
							},
							reqCancelled: (_context, event) => Axios.isCancel(event.data),
						}),
					},
				},
			},

			selectingArea: {
				always: [
					{
						target: 'loadingProducts',
						actions: [
							{ type: 'clearContext', clearAfter: 'area' },
							'setPreSelectedArea',
						],
					},
				],
			},

			loadingProducts: {
				entry: ['updateUrl'],
				invoke: {
					id: 'getProducts',
					src: (context, _event) => loadApiData(context.apiUrl, context),
					onDone: {
						target: 'selectingProduct',
						actions: ['setData', 'setIsSurvey'],
					},
					onError: {
						target: 'errorProduct',
						actions: assign({
							error: (_context, event) => {
								return `Error ${event.data.response.status} - ${event.data.response.statusText}`;
							},
							reqCancelled: (_context, event) => Axios.isCancel(event.data),
						}),
					},
				},
			},

			selectingProduct: {},

			loadingUnits: {
				entry: ['updateUrl', 'setIsSurvey'],
				invoke: {
					id: 'getUnits',
					src: (context, _event) => loadApiData(context.apiUrl, context),
					onDone: {
						target: 'selectingUnit',
						actions: 'setData',
					},
					onError: {
						target: 'errorUnit',
						actions: assign({
							error: (_context, event) => {
								return `Error ${event.data.response.status} - ${event.data.response.statusText}`;
							},
							reqCancelled: (_context, event) => Axios.isCancel(event.data),
						}),
					},
				},
			},

			selectingUnit: {},

			loadingDistributedUnits: {
				entry: ['updateUrl', 'setIsSurvey'],
				invoke: {
					id: 'getDistributedUnits',
					src: (context, _event) => loadApiData(context.apiUrl, context),
					onDone: {
						target: 'selectingDistributedUnit',
						actions: 'setData',
					},
					onError: {
						target: 'errorDistributedUnit',
						actions: assign({
							error: (_context, event) => {
								return `Error ${event.data.response.status} - ${event.data.response.statusText}`;
							},
							reqCancelled: (_context, event) => Axios.isCancel(event.data),
						}),
					},
				},
			},

			selectingDistributedUnit: {},

			loadingWizardStep: {
				entry: ['updateUrl'],
				invoke: {
					id: 'getWizardStepData',
					src: (context, _event) =>
						getWizardStep(context.apiUrl, context, context.numItemsToFetch),
					onDone: {
						target: 'selectingWizardStep',
						actions: ['setWizardStepData', 'cancelPrevRequest'],
					},
					onError: {
						target: 'errorWizardStep',
						actions: assign({
							error: (_context, event) => {
								return `Error ${event.data.response.status} - ${event.data.response.statusText}`;
							},
							reqCancelled: (_context, event) => Axios.isCancel(event.data),
						}),
					},
				},
			},

			selectingWizardStep: {
				always: [
					{
						target: 'checkingWizardProgress',
						cond: (context) =>
							Array.isArray(context.pendingWizardSelections) &&
							context.pendingWizardSelections.length > 0,
					},
				],
				on: {
					SELECT_WIZARD_OPTION: {
						actions: [
							'addWizardSelection',
							'setSelectedWizardOption',
							'clearSubsequentWizardSteps',
							'cancelPrevRequest',
						],
						target: 'checkingWizardProgress',
					},
				},
			},

			checkingWizardProgress: {
				entry: ['cancelPrevRequest'],
				always: [
					{
						target: 'loadingWizardStep',
						cond: 'hasMoreWizardSteps',
						actions: ['updateUrl'],
					},
					{
						target: 'result.loadingStatistics',
						actions: ['setWizardCompletionState'],
					},
				],
			},

			errorWizardStep: {
				on: {
					RETRY: {
						target: 'loadingWizardStep',
					},
				},
			},

			result: {
				id: 'parameters',
				initial: 'loadingStatistics',
				states: {
					loadingStatistics: {
						entry: ['setMeasurementFromWizard', 'updateUrl'],
						invoke: {
							id: 'getResult',
							src: (context, _event) => {
								return loadApiData(context.apiUrl, context);
							},
							onDone: {
								target: 'success',
								actions: 'setData',
							},
							onError: {
								target: 'error',
								actions: assign({
									error: (_context, event) =>
										event.data?.message ||
										event.data?.error ||
										'An unexpected error occurred',
									reqCancelled: (_context, event) => Axios.isCancel(event.data),
								}),
							},
						},
					},

					error: {
						on: {
							RETRY: {
								target: 'loadingStatistics',
							},
						},
						always: [
							{
								target: 'loadingStatistics',
								cond: (context) => context.reqCancelled === true,
							},
						],
					},
					success: {
						entry: ['createResultHeading'],
						always: [
							{
								target: 'loadingStatistics',
								cond: 'canPreSelectParameter',
								actions: 'setPreSelectedParameter',
							},
							{
								target: 'loadingStatistics',
								cond: 'canPreSelectYear',
								actions: 'setPreSelectedYear',
							},
						],
					},

					loadingFilterItems: {
						invoke: {
							id: 'getFilterItems',
							src: (context, _event) => {
								return searchApiData(
									context.apiUrl,
									context,
									_event.itemsToFetch || context.numItemsToFetch,
									_event.filter || '',
									_event.searchterm
								);
							},
							onDone: {
								target: 'success',
								actions: 'setFilterItems',
							},
							onError: {
								target: 'error',
								actions: assign({
									error: (_context, event) => event.data.message,
									reqCancelled: (_context, event) => Axios.isCancel(event.data),
								}),
							},
						},
					},
				},
				on: {
					SET_FILTER: {
						target: '.loadingStatistics',
						actions: ['setFilter', 'cancelPrevRequest'],
					},
					CLEAR_FILTER: {
						target: '.loadingStatistics',
						actions: ['clearFilter', 'cancelPrevRequest'],
					},
					RESET_FILTERS: {
						target: '.loadingStatistics',
						actions: ['resetFilters', 'cancelPrevRequest'],
					},
					ADD_YEAR: {
						target: '.loadingStatistics',
						actions: ['addYear', 'cancelPrevRequest'],
					},
					REMOVE_YEAR: {
						target: '.loadingStatistics',
						actions: ['removeYear', 'cancelPrevRequest'],
					},
					SET_TIME_FRAGMENT: {
						target: '.loadingStatistics',
						actions: ['setTimeFragment', 'cancelPrevRequest'],
					},
					SELECT_MEASUREMENT: {
						target: '.loadingStatistics',
						actions: ['selectMeasurement', 'cancelPrevRequest'],
					},
					SELECT_VARIABLE: {
						target: '.loadingStatistics',
						actions: ['selectVariable', 'cancelPrevRequest'],
					},
					ADD_PARAMETER: {
						target: '.loadingStatistics',
						actions: ['addParameter', 'cancelPrevRequest'],
					},
					REMOVE_PARAMETER: {
						target: '.loadingStatistics',
						actions: ['removeParameter', 'cancelPrevRequest'],
					},
					SELECT_SORT: '.loadingStatistics',
					SELECT_RESULT_FORMAT: {
						target: '.loadingStatistics',
						actions: ['selectResultFormat', 'cancelPrevRequest'],
					},
					SET_CHART_STACKED: {
						target: '.loadingStatistics',
						actions: ['setChartStacked', 'cancelPrevRequest'],
					},
					UNSET_CHART_STACKED: {
						target: '.loadingStatistics',
						actions: ['unsetChartStacked', 'cancelPrevRequest'],
					},
					SELECT_CHART_TYPE: {
						target: '.loadingStatistics',
						actions: ['selectChartType', 'cancelPrevRequest'],
					},
					CHANGE_ORDER: {
						target: '.loadingStatistics',
						actions: ['changeOrder', 'cancelPrevRequest'],
					},
					LOAD_MORE: {
						target: '.loadingStatistics',
						actions: ['loadMore', 'cancelPrevRequest'],
					},
					EXPORT_RESULTS: {
						actions: 'exportingResults',
					},
					SEARCH_FILTER: {
						target: '.loadingFilterItems',
					},
					LOAD_MORE_FILTERITEMS: {
						target: '.loadingFilterItems',
					},
					SELECT_WIZARD_OPTION: {
						actions: [
							'addWizardSelection',
							'setSelectedWizardOption',
							'cancelPrevRequest',
						],
						target: 'checkingWizardProgress',
					},
				},
			},

			errorArea: {
				on: {
					RETRY: {
						target: 'loadingAreas',
					},
				},
			},

			errorProduct: {
				on: {
					RETRY: {
						target: 'loadingProducts',
					},
				},
			},

			errorUnit: {
				on: {
					RETRY: {
						target: 'loadingUnits',
					},
				},
			},
			errorDistributedUnit: {
				on: {
					RETRY: {
						target: 'loadingDistributedUnits',
					},
				},
			},

			errorParameters: {
				on: {
					RETRY: {
						target: 'result',
					},
				},
			},
		},
		on: {
			SELECT_AREA: [
				{
					target: 'loadingProducts',
					actions: [
						{ type: 'clearContext', clearAfter: 'area' },
						'setSelectedArea',
						'setIsSurvey',
						'cancelPrevRequest',
					],
				},
			],
			SELECT_PRODUCT: {
				cond: 'canSelectProduct',
				target: 'loadingUnits',
				actions: [
					{ type: 'clearContext', clearAfter: 'product' },
					'setSelectedProduct',
					'setIsSurvey',
					'cancelPrevRequest',
				],
			},
			SELECT_UNIT: {
				cond: 'canSelectUnit',
				target: 'loadingDistributedUnits',
				actions: [
					{ type: 'clearContext', clearAfter: 'unit' },
					'setSelectedUnit',
					'setIsSurvey',
					'cancelPrevRequest',
				],
			},
			SELECT_DISTRIBUTED_UNIT: [
				{
					cond: (context) =>
						context?.products?.find((p) => p.value === context?.selectedProduct)
							?.isSurvey === true,
					target: 'loadingWizardStep',
					actions: [
						'resetWizardSteps',
						{ type: 'clearContext', clearAfter: 'distributedUnit' },
						'setSelectedDistributedUnit',
						'setIsSurvey',
						'cancelPrevRequest',
					],
				},

				{
					cond: 'canSelectDistributedUnit',
					target: 'result',
					actions: [
						{ type: 'clearContext', clearAfter: 'distributedUnit' },
						'setSelectedDistributedUnit',
						'cancelPrevRequest',
					],
				},
			],

			CLEAR_ALL: {
				target: 'loadingAreas',
				actions: [
					{ type: 'clearContext', clearAfter: 'all' },
					'cancelPrevRequest',
				],
			},
		},
	},
	{
		guards: {
			isCancelled: (context) => {
				return context.reqCancelled;
			},
			canSelectProduct: (context) => {
				return context.selectedArea !== null;
			},
			canSelectUnit: (context) => {
				return context.selectedProduct !== null;
			},
			shouldStartWizard: (context) => {
				return context.isSurvey === true;
			},
			canSelectDistributedUnit: (context) => {
				return context.selectedUnit !== null;
			},
			canPreSelectParameter: (context) => {
				if (
					context.selectedParameters.length === 0 &&
					context.selectedDistributedUnit &&
					(context.selectedDistributedUnit.includes(
						'Upphandlingar, Socialtyp.Antal socialt hållbara upphandlingar'
					) ||
						context.selectedDistributedUnit.includes(
							'Upphandlingar, Innovationstyp.Antal innovationsupphandlingar'
						) ||
						context.selectedDistributedUnit.includes(
							'Upphandlingar, Miljötyp.Antal miljömässigt hållbara upphandlingar'
						))
				) {
					return true;
				}

				return (
					context.selectedParameters.length === 0 &&
					context.parameters?.length === 1 &&
					context.parameters[0]?.dimensions?.length === 1 &&
					context.parameters[0]?.dimensions[0]?.properties?.length === 1
				);
			},
			canPreSelectYear: (context) => {
				return (
					context.selectedYear.length === 0 &&
					context.time?.years?.length !== undefined &&
					context.time?.years?.length >= 0
				);
			},
			hasResult: (context) => {
				return context.result !== null;
			},
			hasAreas: (context) => {
				return context.areas !== null;
			},
			hasProducts: (context) => {
				return context.products !== null;
			},
			hasUnits: (context) => {
				return context.units !== null;
			},
			hasDistributedUnits: (context) => {
				return context.distributedUnits !== null && context.isSurvey === false;
			},
			hasOrganizationsFilter: (context) => {
				return context.organizationsFilter !== null;
			},
			hasQuestionsFilter: (context) => {
				return context.questionsFilter !== null;
			},
			hasCategoriesFilter: (context) => {
				return context.categoriesFilter !== null;
			},
			hasSelectedCategoryFilter: (context) => {
				return context.selectedCategoryFilter !== null;
			},
			hasSelectedQuestionFilter: (context) => {
				return context.selectedQuestionFilter !== null;
			},
			hasSelectedOrganizationFilter: (context) => {
				return (
					context.selectedOrganizationFilter !== null &&
					Array.isArray(context.selectedOrganizationFilter) &&
					context.selectedOrganizationFilter.length > 0
				);
			},

			// New guards for dynamic wizard steps
			hasMoreWizardSteps: (context) => {
				// Check if the current step (just completed) indicates more steps are available
				const lastStepIndex = context.wizardSteps.length - 1;
				if (lastStepIndex < 0) return false;

				const lastStep = context.wizardSteps[lastStepIndex];
				const hasMoreStepsAvailable = lastStep?.hasMore === true;
				const hasPendingSelections =
					Array.isArray(context.pendingWizardSelections) &&
					context.pendingWizardSelections.length > 0;
				const withinMaxSteps =
					context.wizardSteps.length < context.maxWizardSteps;

				return (
					(hasMoreStepsAvailable || hasPendingSelections) && withinMaxSteps
				);
			},
			wizardStepsComplete: (context) => {
				if (context.wizardSteps.length === 0) return false;

				const lastStepIndex = context.wizardSteps.length - 1;
				const lastStep = context.wizardSteps[lastStepIndex];

				return (
					context.wizardSteps.length >= context.maxWizardSteps ||
					lastStep?.hasMore !== true
				);
			},
		},
		actions: {
			setWizardStepData: assign((context: Context, event: any) => {
				const paramKey = event.data?.current?.paramKey;
				const label = event.data?.current?.label;
				const isMultiSelect = event.data?.current?.isMultiSelect ?? false;
				const options =
					event.data?.current?.options?.map((option: any) => ({
						label: option.label,
						value: option.value,
						count: option.count,
					})) || [];

				if (!paramKey || !label || options.length === 0) {
					console.warn('Invalid wizard step data:', {
						paramKey,
						label,
						options,
					});
					return {
						wizardSteps: context.wizardSteps,
						wizardFilters: context.wizardFilters,
						wizardStepInfo: event.data,
						wizardStep: context.wizardStep,
						currentWizardStepIndex: context.currentWizardStepIndex,
						wizardSelections: context.wizardSelections,
						pendingWizardSelections: context.pendingWizardSelections,
						selectedFilters: context.selectedFilters,
						selectedParameters: context.selectedParameters,
						wizardFilterParamKeys: context.wizardFilterParamKeys,
						selectedCategoryFilter: context.selectedCategoryFilter,
						selectedQuestionFilter: context.selectedQuestionFilter,
						selectedOrganizationFilter: context.selectedOrganizationFilter,
					};
				}

				const hasMore = !!(
					event.data?.next && Object.keys(event.data.next).length > 0
				);

				const newStep = {
					paramKey,
					label,
					options,
					hasMore,
					isMultiSelect,
				};

				const wizardSteps = [...context.wizardSteps, newStep];
				const currentWizardStepIndex = context.currentWizardStepIndex + 1;
				const pendingSelections = [...(context.pendingWizardSelections || [])];
				const pendingSelectionIndex = pendingSelections.findIndex(
					(selection) => selection.paramKey === paramKey
				);

				let wizardSelections = [...context.wizardSelections];
				let selectedFilters = [...context.selectedFilters];
				let selectedParameters = [...context.selectedParameters];
				let wizardFilterParamKeys = [...(context.wizardFilterParamKeys || [])];
				let selectedCategoryFilter = context.selectedCategoryFilter;
				let selectedQuestionFilter = context.selectedQuestionFilter;
				let selectedOrganizationFilter = context.selectedOrganizationFilter;

				if (pendingSelectionIndex !== -1) {
					const pendingSelection = pendingSelections[pendingSelectionIndex];
					const validValues = pendingSelection.values.filter((value) =>
						options.some((option: WizardStepOption) => option.value === value)
					);

					if (validValues.length > 0) {
						wizardSelections[wizardSteps.length - 1] = isMultiSelect
							? validValues
							: validValues[0];

						selectedFilters = selectedFilters.filter(
							(filter: string) => !filter.startsWith(`${paramKey}|`)
						);
						selectedFilters.push(
							...validValues.map((value) => `${paramKey}|${value}`)
						);

						selectedParameters = [
							...selectedParameters.filter((param) => param !== paramKey),
							paramKey,
						];

						wizardFilterParamKeys = [
							...wizardFilterParamKeys.filter((key) => key !== paramKey),
							paramKey,
						];

						if (paramKey === 'categories' || paramKey?.includes('Category')) {
							selectedCategoryFilter = validValues[0];
						}

						if (paramKey === 'questions' || paramKey?.includes('Question')) {
							selectedQuestionFilter = validValues[0];
						}

						if (
							paramKey === 'organizations' ||
							paramKey?.includes('Organization')
						) {
							selectedOrganizationFilter = validValues;
						}
					}

					pendingSelections.splice(pendingSelectionIndex, 1);
				}

				return {
					wizardSteps,
					wizardFilters: {
						text: label,
						type: paramKey || `wizard_step_${context.wizardSteps.length}`,
						options,
						isMultiSelect,
					},
					wizardStepInfo: event.data,
					wizardStep: context.wizardStep + 1,
					currentWizardStepIndex,
					wizardSelections,
					pendingWizardSelections: pendingSelections,
					selectedFilters,
					selectedParameters,
					wizardFilterParamKeys,
					selectedCategoryFilter,
					selectedQuestionFilter,
					selectedOrganizationFilter,
				};
			}) as any,
			addWizardSelection: assign({
				wizardSelections: (context, event: any) => {
					const newSelections = [...context.wizardSelections];
					// Get step index from event data or use current step
					const stepIndex =
						event.data?.stepIndex !== undefined
							? event.data.stepIndex
							: context.currentWizardStepIndex - 1;
					const selectionValue =
						event.data?.value !== undefined ? event.data.value : event.data;

					// Check if this step is multiselect
					const step = context.wizardSteps[stepIndex];
					const isMultiSelect = step?.isMultiSelect ?? false;

					if (isMultiSelect) {
						// For multiselect steps, if value is already an array (from MultiSelectDropdown),
						// store it as-is. Otherwise, toggle the value in an array.
						if (Array.isArray(selectionValue)) {
							// Store the complete array from multiselect component
							newSelections[stepIndex] = selectionValue;
						} else {
							// Toggle behavior for individual checkbox selections
							const currentSelections = Array.isArray(newSelections[stepIndex])
								? [...newSelections[stepIndex]]
								: [];
							const valueIndex = currentSelections.indexOf(selectionValue);
							if (valueIndex !== -1) {
								// Remove if already selected
								currentSelections.splice(valueIndex, 1);
							} else {
								// Add if not selected
								currentSelections.push(selectionValue);
							}
							newSelections[stepIndex] = currentSelections;
						}
					} else {
						// For single select steps, store as a single value
						newSelections[stepIndex] = selectionValue;
					}
					return newSelections;
				},
			}),
			resetWizardSteps: assign((context: Context) => ({
				wizardSteps: [],
				wizardSelections: [],
				pendingWizardSelections: [],
				wizardFilters: null,
				wizardFilterParamKeys: [],
				currentWizardStepIndex: 0,
				wizardStepInfo: null,
				isWizardActive: false,
			})) as any,

			setWizardCompletionState: assign((context: Context) => ({
				isWizardActive: false,
			})) as any,
			clearSubsequentWizardSteps: assign((context: Context) => {
				// Get the current step index (1-based)
				const currentStepIndex = context.currentWizardStepIndex;

				// Keep only steps up to and including the current step
				const clearedWizardSteps = context.wizardSteps.slice(
					0,
					currentStepIndex
				);

				// Keep only selections up to and including the current step
				const clearedWizardSelections = context.wizardSelections.slice(
					0,
					currentStepIndex
				);

				return {
					wizardSteps: clearedWizardSteps,
					wizardSelections: clearedWizardSelections,
				};
			}) as any,
			setSelectedWizardOption: assign((context: Context, event: any) => {
				// Get step index from event data or use current step
				const stepIndex =
					event.data?.stepIndex !== undefined
						? event.data.stepIndex
						: context.currentWizardStepIndex - 1;
				const selectionValue =
					event.data?.value !== undefined ? event.data.value : event.data;

				// FIX #4: Validate step and paramKey exist before proceeding
				const currentStep = context.wizardSteps[stepIndex];
				if (
					!currentStep ||
					!currentStep.paramKey ||
					typeof currentStep.paramKey !== 'string'
				) {
					console.warn(
						`Invalid wizard step at index ${stepIndex}:`,
						currentStep
					);
					return context as any;
				}

				const paramKey = currentStep.paramKey;

				// Only clear dependent data if we're changing the current step or a past step
				if (stepIndex < context.currentWizardStepIndex - 1) {
					// User is changing a past step, need to reset to that step and clear all dependent data
					// But preserve filters from all steps up to and including the one being changed
					const keptWizardSelections = context.wizardSelections.slice(
						0,
						stepIndex + 1
					);
					const keptWizardSteps = context.wizardSteps.slice(0, stepIndex + 1);

					// Rebuild selectedFilters from kept wizard selections
					const keptSelectedFilters: string[] = [];
					const keptSelectedParameters: string[] = [];
					const keptWizardFilterParamKeys: string[] = [];

					keptWizardSteps.forEach((step, index) => {
						if (step.paramKey && keptWizardSelections[index]) {
							const pKey = step.paramKey;
							const selVal = keptWizardSelections[index];
							const selArray = Array.isArray(selVal) ? selVal : [selVal];

							selArray.forEach((val: string) => {
								keptSelectedFilters.push(`${pKey}|${val}`);
							});

							keptSelectedParameters.push(pKey);
							keptWizardFilterParamKeys.push(pKey);
						}
					});

					return {
						...context,
						currentWizardStepIndex: stepIndex + 1,
						wizardSteps: keptWizardSteps,
						wizardSelections: keptWizardSelections,
						pendingWizardSelections: [],
						wizardFilters: null,
						// Clear all results and related data
						result: null,
						resultError: null,
						// Preserve filters from kept wizard steps
						selectedFilters: keptSelectedFilters,
						selectedParameters: keptSelectedParameters,
						wizardFilterParamKeys: keptWizardFilterParamKeys,
						selectedCategoryFilter: null,
						selectedQuestionFilter: null,
						selectedOrganizationFilter: null,
						selectedVariable: null,
						selectedMeasurement: null,
						// Clear time selections
						selectedYear: [],
						selectedTimeFragment: ['Tidsperiod.År'],
						// Clear other data that depends on wizard selections
						parameters: null,
						variables: null,
						time: null,
						filters: null,
						searchFilters: null,
						measurements: null,
						selectedResultFormat: 'table',
						selectedChartType: 'column',
						selectedChartStacked: false,
						numItemsToFetch: 2000,
						numItemsTotal: 0,
						order: [],
						categoriesFilter: null,
						questionsFilter: null,
						organizationsFilter: null,
					};
				}

				// Filter out any existing filters with the same paramKey
				const filteredFilters = context.selectedFilters.filter(
					(filter: string) => !filter.startsWith(`${paramKey}|`)
				);

				// Filter out any existing parameters with the same paramKey
				const filteredParameters = (context.selectedParameters as any).filter(
					(param: string) => param !== paramKey
				);

				// Handle multi-select (for organizations)
				const selectionArray = Array.isArray(selectionValue)
					? selectionValue
					: [selectionValue];
				const newFilters = selectionArray.map(
					(val: string) => `${paramKey}|${val}`
				);

				return {
					selectedFilters: [...filteredFilters, ...newFilters],
					selectedParameters: [...filteredParameters, paramKey],
					wizardFilterParamKeys: [
						...(context.wizardFilterParamKeys || []),
						paramKey,
					].filter((v, i, a) => a.indexOf(v) === i),
					selectedCategoryFilter:
						paramKey === 'categories' || paramKey?.includes('Category')
							? selectionArray[0] || null
							: context.selectedCategoryFilter,
					selectedQuestionFilter:
						paramKey === 'questions' || paramKey?.includes('Question')
							? selectionArray[0] || null
							: context.selectedQuestionFilter,
					selectedOrganizationFilter:
						paramKey === 'organizations' || paramKey?.includes('Organization')
							? selectionArray
							: context.selectedOrganizationFilter,
					result: null,
					resultError: null,
					selectedVariable: null,
					selectedMeasurement: context.measurements
						? context.measurements[0].value
						: null,
					selectedYear: [],
					selectedTimeFragment: ['Tidsperiod.År'],
				};
			}) as any,
			setData: assign({
				areas: (_context, event) => event.data.areas,
				products: (_context, event) => event.data.products,
				units: (_context, event) => event.data.units,
				distributedUnits: (_context, event) => event.data.distributedUnits,
				time: (_context, event) => event.data.time,
				parameters: (_context, event) =>
					event.data.parameters || _context.parameters,
				filters: (_context, event) => event.data.filters,
				measurements: (_context, event) => event.data.measurements,
				variables: (_context, event) => event.data.variables,
				result: (_context, event) => event.data.result,
				resultError: (_context, event) => event.data.resultError,
				selectedMeasurement: (context, event) => {
					if (context.selectedMeasurement) {
						return context.selectedMeasurement;
					} else if (event.data.measurements && event.data.measurements[0]) {
						return event.data.measurements[0].value;
					}
					return null;
				},
				// FIX #5: Enhanced isSurvey logic - validate against fresh data, default to false
				isSurvey: (context, event) => {
					// Priority 1: Check newly loaded products
					if (
						event.data?.products &&
						Array.isArray(event.data.products) &&
						context.selectedProduct
					) {
						const selectedProductData = event.data.products.find(
							(p: OptionData) => p.value === context.selectedProduct
						);
						if (
							selectedProductData &&
							typeof selectedProductData.isSurvey === 'boolean'
						) {
							return selectedProductData.isSurvey;
						}
					}

					// Priority 2: Check existing products if not in new data
					if (
						context.products &&
						Array.isArray(context.products) &&
						context.selectedProduct
					) {
						const selectedProductData = context.products.find(
							(p: OptionData) => p.value === context.selectedProduct
						);
						if (
							selectedProductData &&
							typeof selectedProductData.isSurvey === 'boolean'
						) {
							return selectedProductData.isSurvey;
						}
					}

					// Priority 3: Default to false if uncertain
					return false;
				},
				numItemsToFetch: (context, event) =>
					event.data.result?.fetch || context.numItemsToFetch,
				numItemsTotal: (context, event) =>
					event.data.result?.total || context.numItemsTotal,
				lastUpdated: (context, event) =>
					event.data.lastUpdated || context.lastUpdated,
				error: (context, event) => {
					return null;
				},
				reqCancelled: (context, event) => false,
				selectedTimeFragment: (context, event) => {
					if (context.selectedTimeFragment.length > 1) {
						return [context.selectedTimeFragment[1]];
					} else {
						return context.selectedTimeFragment;
					}
				},
			}),
			createResultHeading: assign({
				resultHeading: (context, event) => {
					let resultHeading = '';
					let filters: any = [];

					if (context.selectedUnit && context.units) {
						resultHeading +=
							context.units.filter(
								(item) => item.value === context.selectedUnit
							)[0].text + ' ';
					}
					if (context.selectedTimeFragment && context.time?.fragments) {
						filters.push(
							context.selectedTimeFragment[0]?.substring(
								context.selectedTimeFragment[0]?.indexOf('.') + 1
							)
						);
					}
					if (context.selectedParameters && context.parameters) {
						for (const key in context.parameters) {
							const paramGroup: any = context.parameters[key].dimensions;
							for (const key2 in paramGroup) {
								const properties: any = paramGroup[key2].properties;
								const matches = properties.filter((item: OptionData) =>
									context.selectedParameters.indexOf(item.value) !== -1
										? item
										: null
								);
								if (matches.length > 0) {
									matches.forEach((item: any) => {
										filters.push(item.text);
									});
								}
							}
						}
					}

					filters = filters.map((item: any) => item?.toLowerCase());
					if (filters.length > 1) {
						const lastFilter = filters.pop();
						resultHeading = `${resultHeading} efter ${filters.join(
							', '
						)} och ${lastFilter}`;
					} else if (filters.length === 1) {
						resultHeading = `${resultHeading} efter ${filters.join(', ')}`;
					}
					return resultHeading;
				},
			}),
			setSelectedArea: assign({
				selectedArea: (_context, event) => {
					return event.data;
				},
			}),
			setSelectedProduct: assign({
				selectedProduct: (_context, event) => event.data,
			}),
			setSelectedUnit: assign({
				selectedUnit: (_context, event) => event.data,
			}),
			setIsSurvey: assign({
				isSurvey: (context, event) => {
					const product = context.products?.find(
						(a) => a.value === context.selectedProduct
					);
					return typeof product?.isSurvey === 'boolean'
						? product.isSurvey
						: false;
				},
			}),
			setSelectedDistributedUnit: assign({
				selectedDistributedUnit: (_context, event) => event.data,
			}),
			// Temporary actions - setPreSelectedArea
			setPreSelectedArea: assign({
				selectedArea: (context, _event) => {
					return context.areas?.[0]?.value ?? null;
				},
			}),
			setFilter: assign({
				selectedFilters: (context, event) => {
					return [...context.selectedFilters, event.value];
				},
			}),
			setFilterItems: assign({
				filters: (context, event) => {
					context.filters?.map((filter: OptionDataFiltersGroup) => {
						if (event.data.items && filter.text === event.data.text) {
							filter.items = event.data.items;
						}
					});

					return context.filters;
				},
			}),
			clearFilter: assign({
				selectedFilters: (context, event) => {
					return context.selectedFilters.filter((val) => val !== event.value);
				},
			}),
			resetFilters: assign({
				selectedFilters: (context, event) => {
					// If wizard was used, keep only wizard-selected filters and clear manual filters
					// If no wizard (normal flow), keep all filters as-is
					if (
						context.wizardFilterParamKeys &&
						context.wizardFilterParamKeys.length > 0
					) {
						// Wizard flow: Keep only wizard-selected filters
						const wizardFilters: string[] = [];

						for (const filter of context.selectedFilters) {
							if (filter.includes('|')) {
								const paramKey = filter.split('|')[0];
								// Keep if paramKey came from wizard steps
								if (context.wizardFilterParamKeys.includes(paramKey)) {
									wizardFilters.push(filter);
								}
							}
						}

						return wizardFilters;
					} else {
						// Normal flow (no wizard): Clear all filters
						return [];
					}
				},
			}),
			addYear: assign({
				selectedYear: (context, event) => {
					return [...context.selectedYear, event.data];
				},
			}),
			removeYear: assign({
				selectedYear: (context, event) => {
					return context.selectedYear.filter((val) => val !== event.data);
				},
			}),
			setMeasurementFromWizard: assign({
				measurements: (context) => {
					if (
						context.wizardSteps.length > 0 &&
						context.wizardStepInfo?.measurements &&
						context.wizardStepInfo.measurements.length > 0
					) {
						return context.wizardStepInfo.measurements;
					}
					return context.measurements;
				},
				selectedMeasurement: (context) => {
					if (
						context.wizardSteps.length > 0 &&
						context.wizardStepInfo?.measurements &&
						context.wizardStepInfo.measurements.length > 0
					) {
						// If the user already selected a measurement that exists
						// in the available wizard measurements, keep their selection
						const isCurrentSelectionValid =
							context.selectedMeasurement &&
							context.wizardStepInfo.measurements.some(
								(m: OptionData) => m.value === context.selectedMeasurement
							);
						if (isCurrentSelectionValid) {
							return context.selectedMeasurement;
						}
						return context.wizardStepInfo.measurements[0].value;
					}
					return context.selectedMeasurement;
				},
			}),
			selectMeasurement: assign({
				selectedMeasurement: (_context, event) => event.data,
			}),
			selectVariable: assign({
				selectedVariable: (_context, event) => event.data,
			}),
			selectResultFormat: assign({
				selectedResultFormat: (_context, event) => event.data,
			}),
			selectChartType: assign({
				selectedChartType: (_context, event) => event.data,
			}),
			changeOrder: assign({
				order: (_context, event) => `${event.orderBy}_${event.orderDirection}`,
			}),
			loadMore: assign({
				numItemsToFetch: (context, _event) =>
					context.numItemsToFetch + context.itemsPerPage,
			}),
			setChartStacked: assign({
				selectedChartStacked: (_context, _event) => true,
			}),
			unsetChartStacked: assign({
				selectedChartStacked: (_context, _event) => false,
			}),
			addParameter: assign({
				selectedParameters: (context, event) => {
					return [...context.selectedParameters, event.data];
				},
			}),
			removeParameter: assign({
				selectedParameters: (context, event) => {
					return context.selectedParameters.filter((val) => val !== event.data);
				},
				selectedFilters: (context, event) => {
					return context.selectedFilters.filter(
						(val: string) => !val.includes(`${event.data}|`)
					);
				},
			}),
			setPreSelectedParameter: assign({
				selectedParameters: (context, event) => {
					if (
						context.parameters &&
						context.selectedDistributedUnit &&
						context.selectedDistributedUnit.includes(
							'Upphandlingar, Socialtyp.Antal socialt hållbara upphandlingar'
						)
					) {
						return [
							'Typ av socialt hållbar upphandling.Typ av socialt hållbar upphandling',
						];
					} else if (
						context.parameters &&
						context.selectedDistributedUnit &&
						context.selectedDistributedUnit.includes(
							'Upphandlingar, Innovationstyp.Antal innovationsupphandlingar'
						)
					) {
						return [
							'Typ av innovationsupphandling.Typ av innovationsupphandling',
						];
					} else if (
						context.parameters &&
						context.selectedDistributedUnit &&
						context.selectedDistributedUnit.includes(
							'Upphandlingar, Miljötyp.Antal miljömässigt hållbara upphandlingar'
						)
					) {
						return [
							'Typ av miljömässigt hållbar upphandling.Typ av miljömässigt hållbar upphandling',
						];
					}

					if (
						context.parameters &&
						context.parameters[0].dimensions &&
						context.parameters[0].dimensions[0].properties &&
						context.parameters[0].dimensions[0].properties[0].value
					) {
						return [context.parameters[0].dimensions[0].properties[0].value];
					} else {
						return [];
					}
				},
			}),
			setPreSelectedYear: assign({
				selectedYear: (context, event) => {
					if (context.time && context.time.years && context.time.years[0]) {
						return [context.time.years[0].value];
					} else {
						return [];
					}
				},
			}),
			setTimeFragment: assign({
				selectedTimeFragment: (_context, event) => {
					return [event.data];
				},
			}),
			clearContext: assign((_context, _event, { action }) => {
				switch (action.clearAfter) {
					case 'all':
						return {
							areas: null,
							products: null,
							parameters: null,
							time: null,
							filters: null,
							searchFilters: null,
							measurements: null,
							units: null,
							distributedUnits: null,
							selectedProduct: null,
							selectedUnit: null,
							selectedDistributedUnit: null,
							selectedMeasurement: null,
							selectedVariable: null,
							selectedFilters: [],
							selectedYear: [],
							selectedTimeFragment: ['Tidsperiod.År'],
							selectedParameters: [],
							selectedResultFormat: 'table',
							selectedChartType: 'column',
							selectedChartStacked: false,
							numItemsToFetch: 2000,
							result: null,
							categoriesFilter: null,
							selectedCategoryFilter: null,
							questionsFilter: null,
							selectedQuestionFilter: null,
							organizationsFilter: null,
							selectedOrganizationFilter: null,
							wizardStep: 0,
							wizardStepInfo: null,
							wizardSteps: [],
							wizardSelections: [],
							pendingWizardSelections: [],
							wizardFilters: null,
							wizardFilterParamKeys: [],
							currentWizardStepIndex: 0,
							isWizardActive: false,
							isSurvey: false,
						};
					case 'area':
						return {
							products: null,
							parameters: null,
							time: null,
							filters: null,
							searchFilters: null,
							measurements: null,
							units: null,
							distributedUnits: null,
							selectedProduct: null,
							selectedUnit: null,
							selectedDistributedUnit: null,
							selectedMeasurement: null,
							selectedVariable: null,
							selectedFilters: [],
							selectedYear: [],
							selectedTimeFragment: ['Tidsperiod.År'],
							selectedParameters: [],
							selectedResultFormat: 'table',
							selectedChartType: 'column',
							selectedChartStacked: false,
							numItemsToFetch: 2000,
							result: null,
							resultError: null,
							categoriesFilter: null,
							selectedCategoryFilter: null,
							questionsFilter: null,
							selectedQuestionFilter: null,
							organizationsFilter: null,
							selectedOrganizationFilter: null,
							wizardStep: 0,
							wizardStepInfo: null,
							wizardSteps: [],
							wizardSelections: [],
							pendingWizardSelections: [],
							wizardFilters: null,
							wizardFilterParamKeys: [],
							currentWizardStepIndex: 0,
							isWizardActive: false,
							isSurvey: false,
						};
					case 'product':
						return {
							parameters: null,
							time: null,
							filters: null,
							searchFilters: null,
							measurements: null,
							units: null,
							distributedUnits: null,
							selectedUnit: null,
							selectedDistributedUnit: null,
							selectedMeasurement: null,
							selectedVariable: null,
							selectedFilters: [],
							selectedYear: [],
							selectedTimeFragment: ['Tidsperiod.År'],
							selectedParameters: [],
							selectedResultFormat: 'table',
							selectedChartType: 'column',
							selectedChartStacked: false,
							numItemsToFetch: 2000,
							result: null,
							resultError: null,
							categoriesFilter: null,
							selectedCategoryFilter: null,
							questionsFilter: null,
							selectedQuestionFilter: null,
							organizationsFilter: null,
							selectedOrganizationFilter: null,
							wizardStep: 0,
							wizardStepInfo: null,
							wizardSteps: [],
							wizardSelections: [],
							pendingWizardSelections: [],
							wizardFilters: null,
							wizardFilterParamKeys: [],
							currentWizardStepIndex: 0,
							isWizardActive: false,
							isSurvey: false,
						};
					case 'unit':
						return {
							parameters: null,
							time: null,
							filters: null,
							searchFilters: null,
							measurements: null,
							distributedUnits: null,
							selectedDistributedUnit: null,
							selectedMeasurement: null,
							selectedVariable: null,
							selectedFilters: [],
							selectedYear: [],
							selectedTimeFragment: ['Tidsperiod.År'],
							selectedParameters: [],
							selectedResultFormat: 'table',
							selectedChartType: 'column',
							selectedChartStacked: false,
							resultError: null,
							categoriesFilter: null,
							selectedCategoryFilter: null,
							questionsFilter: null,
							selectedQuestionFilter: null,
							organizationsFilter: null,
							selectedOrganizationFilter: null,
							wizardStep: 0,
							wizardStepInfo: null,
							wizardSteps: [],
							wizardSelections: [],
							pendingWizardSelections: [],
							wizardFilters: null,
							wizardFilterParamKeys: [],
							currentWizardStepIndex: 0,
							isWizardActive: false,
							isSurvey: false,
						};
					case 'distributedUnit':
						return {
							parameters: null,
							time: null,
							filters: null,
							searchFilters: null,
							measurements: null,
							selectedMeasurement: null,
							selectedVariable: null,
							selectedFilters: [],
							selectedYear: [],
							selectedTimeFragment: ['Tidsperiod.År'],
							selectedParameters: [],
							selectedResultFormat: 'table',
							selectedChartType: 'column',
							selectedChartStacked: false,
							resultError: null,
							categoriesFilter: null,
							selectedCategoryFilter: null,
							questionsFilter: null,
							selectedQuestionFilter: null,
							organizationsFilter: null,
							selectedOrganizationFilter: null,
							wizardStep: 0,
							wizardStepInfo: null,
							wizardSteps: [],
							wizardSelections: [],
							pendingWizardSelections: [],
							wizardFilters: null,
							wizardFilterParamKeys: [],
							currentWizardStepIndex: 0,
							isWizardActive: false,
							isSurvey: false,
						};
					case 'categoriesfilter':
						return {
							parameters: null,
							variables: null,
							time: null,
							filters: null,
							searchFilters: null,
							measurements: null,
							selectedMeasurement: null,
							selectedVariable: null,
							selectedYear: [],
							selectedTimeFragment: ['Tidsperiod.År'],
							selectedParameters: [],
							selectedResultFormat: 'table',
							selectedChartType: 'column',
							selectedChartStacked: false,
							result: null,
							resultError: null,
						};
					case 'questionsfilter':
						return {
							parameters: null,
							variables: null,
							time: null,
							filters: null,
							searchFilters: null,
							measurements: null,
							selectedMeasurement: null,
							selectedVariable: null,
							selectedYear: [],
							selectedTimeFragment: ['Tidsperiod.År'],
							selectedParameters: [],
							selectedResultFormat: 'table',
							selectedChartType: 'column',
							selectedChartStacked: false,
							result: null,
							resultError: null,
						};
					case 'organizationsfilter':
						return {
							parameters: null,
							variables: null,
							time: null,
							filters: null,
							searchFilters: null,
							measurements: null,
							selectedMeasurement: 'SUM',
							selectedVariable: null,
							selectedYear: [],
							selectedTimeFragment: ['Tidsperiod.År'],
							selectedParameters: [],
							selectedResultFormat: 'table',
							selectedChartType: 'column',
							selectedChartStacked: false,
							result: null,
							resultError: null,
						};
					case 'parameters':
						return {
							selectedMeasurement: null,
							selectedVariable: null,
							selectedYear: [],
							selectedTimeFragment: ['Tidsperiod.År'],
							selectedParameters: [],
							selectedFilters: [],
						};
					default:
						return {};
				}
			}),

			setContextFromQuery: assign((context, _event) => {
				const {
					area,
					product,
					unit,
					distributedUnit,
					measurement,
					variable,
					year,
					timefragment,
					param,
					resultFormat,
					chartType,
					chartStacked,
					orderBy,
					fetch: fetchProp,
					total,
					filter,
					category,
					question,
					organization,
				} = parse(window.location.search);
				// FIX #6: Validate URL parameters against available data
				const validateOption = (
					value: any,
					availableOptions: OptionData[] | null | undefined
				): string | null => {
					if (!value || typeof value !== 'string') return null;
					if (!availableOptions || !Array.isArray(availableOptions))
						return typeof value === 'string' ? value : null;
					return availableOptions.some((opt) => opt.value === value)
						? value
						: null;
				};

				const validateOptionArray = (
					value: any,
					availableOptions: OptionData[] | null | undefined
				): string[] => {
					if (!availableOptions || !Array.isArray(availableOptions)) return [];
					const arr = Array.isArray(value)
						? value
						: typeof value === 'string'
						? [value]
						: [];
					return arr.filter((v) =>
						availableOptions.some((opt) => opt.value === v)
					);
				};

				// Validate area
				const validatedArea = validateOption(area, context.areas);

				// Validate product
				const validatedProduct = validatedArea
					? validateOption(product, context.products)
					: null;

				// Validate unit
				const validatedUnit = validatedProduct
					? validateOption(unit, context.units)
					: null;

				// Validate distributed unit
				const validatedDistributedUnit = validatedUnit
					? validateOption(distributedUnit, context.distributedUnits)
					: null;

				// Validate measurement
				const validatedMeasurement = validateOption(
					measurement,
					context.measurements
				);

				// Validate variable
				const validatedVariable = validateOption(variable, context.variables);

				// Validate years
				const validatedYear = context.time?.years
					? validateOptionArray(year, context.time.years)
					: Array.isArray(year)
					? year
					: typeof year === 'string'
					? [year]
					: [];

				// Validate time fragment
				const validatedTimeFragment = context.time?.fragments
					? validateOptionArray(timefragment, context.time.fragments)
					: ['Tidsperiod.År'];

				// Validate parameters
				const validatedParameters = context.parameters
					? validateOptionArray(
							param,
							context.parameters.flatMap(
								(pg) => pg.dimensions?.flatMap((d) => d.properties || []) || []
							)
					  )
					: Array.isArray(param)
					? param
					: typeof param === 'string'
					? [param]
					: [];

				// Validate result format
				const validResultFormats: resultFormats[] = [
					'table',
					'matrixtable',
					'chart',
				];
				const validatedResultFormat = (
					validResultFormats.includes(resultFormat as resultFormats)
						? resultFormat
						: 'table'
				) as resultFormats;

				// Validate chart type
				const validChartTypes: chartTypes[] = ['bar', 'column', 'pie', 'line'];
				const validatedChartType = (
					validChartTypes.includes(chartType as chartTypes)
						? chartType
						: 'column'
				) as chartTypes;

				const selectedFiltersFromUrl = Array.isArray(filter)
					? (filter as string[]).filter(
							(f) => typeof f === 'string' && f.includes('|')
					  )
					: typeof filter === 'string' && filter.includes('|')
					? [filter]
					: [];

				const wizardSelectionMap = selectedFiltersFromUrl.reduce(
					(acc: Record<string, string[]>, currentFilter: string) => {
						const [paramKey, ...valueParts] = currentFilter.split('|');
						const value = valueParts.join('|');

						if (!paramKey || !value) {
							return acc;
						}

						if (!acc[paramKey]) {
							acc[paramKey] = [];
						}

						acc[paramKey].push(value);
						return acc;
					},
					{}
				);

				const wizardFilterParamKeysFromUrl = Object.keys(wizardSelectionMap);
				const pendingWizardSelections = wizardFilterParamKeysFromUrl.map(
					(paramKey) => ({
						paramKey,
						values: wizardSelectionMap[paramKey],
					})
				);

				return {
					...context,
					selectedArea: validatedArea,
					selectedProduct: validatedProduct,
					selectedUnit: validatedUnit,
					selectedCategoryFilter: (category as string) || null,
					selectedQuestionFilter: (question as string) || null,
					selectedOrganizationFilter:
						typeof organization === 'string'
							? (organization as string)
									.split(':')
									.filter((value) => value.length > 0)
							: Array.isArray(organization)
							? (organization as string[])
							: [],
					selectedDistributedUnit: validatedDistributedUnit,
					selectedMeasurement: validatedMeasurement,
					selectedVariable: validatedVariable,
					selectedYear: validatedYear,
					selectedTimeFragment:
						validatedTimeFragment.length > 0
							? validatedTimeFragment
							: ['Tidsperiod.År'],
					selectedParameters: [
						...validatedParameters,
						...wizardFilterParamKeysFromUrl.filter(
							(paramKey) => !validatedParameters.includes(paramKey)
						),
					],
					selectedFilters: selectedFiltersFromUrl,
					selectedResultFormat: validatedResultFormat,
					selectedChartType: validatedChartType,
					selectedChartStacked: chartStacked === 'true' || false,
					order: (orderBy as string) || null,
					numItemsToFetch:
						parseInt(fetchProp as string, 10) || context.itemsPerPage,
					numItemsTotal: parseInt(total as string, 10) || 0,
					wizardSelections: [],
					pendingWizardSelections,
					wizardFilterParamKeys: wizardFilterParamKeysFromUrl,
					wizardSteps: [],
					wizardFilters: null,
					currentWizardStepIndex: 0,
					isWizardActive: pendingWizardSelections.length > 0,
				};
			}),
			// Update the url according to the pager
			updateUrl: (context, _event) => {
				if (context.router) {
					context.router.replace(getUrlQueries(context));
				}
			},
			cancelPrevRequest: assign({
				cancelTokenSource: (context) => {
					if (context.cancelTokenSource) {
						context.cancelTokenSource.cancel();
					}
					return axios.CancelToken.source();
				},
			}),
			exportingResults: (context, _event) => {
				let fileUrl = '';

				switch (_event.value) {
					case 'csv':
					case 'excel':
						if (_event.value === 'csv') {
							fileUrl = `${context.apiUrl}/export/csv${getUrlQueries(context)}`;
						} else {
							fileUrl = `${context.apiUrl}/export/excel${getUrlQueries(
								context
							)}`;
						}
						exportDataByUrl(fileUrl);
						break;
					case 'svg':
						exportDataHighcharts(_event.chartRef, 'image/svg+xml');
						break;
					case 'png':
					case 'jpeg':
						exportDataHighcharts(_event.chartRef, `image/${_event.value}`);
						break;
					default:
						break;
				}
			},
		},
	}
);
